import { useEffect, useId, useRef, useState } from 'react';
import { platformZapierSettingsApi } from '../api.js';
import { ConfirmationDialog, useConfirmationDialog } from './ConfirmationDialog.jsx';
import { canManagePlatformZapierSettings, checkedPlatformZapierSettings, checkedPlatformZapierSettingsSave,
  canSavePlatformZapierSettings, platformZapierReadinessMessage } from '../platform-zapier-settings.js';

export function PlatformZapierSettingsView({ snapshot, draft = false, phase = 'loading', notice = '', confirming = false,
  onChange, onSave, onReload, feedbackRef }) {
  const id = useId();
  const busy = ['loading', 'saving'].includes(phase) || confirming;
  const confirmed = phase === 'ready';
  const canSave = confirmed && !busy && canSavePlatformZapierSettings(snapshot, draft);
  const status = phase === 'loading' ? 'Checking…' : phase === 'saving' ? 'Saving…'
    : !confirmed ? phase === 'unknown' ? 'Unconfirmed' : 'Unavailable'
      : snapshot.effectiveEnabled ? 'On' : snapshot.enabled ? 'Not active' : 'Off';
  return <section className="management-registration" aria-labelledby={`${id}-title`}>
    <div className="management-registration-heading"><h2 id={`${id}-title`}>Zapier availability</h2>
      <span className="management-status" data-status={confirmed && snapshot.effectiveEnabled ? 'active' : confirmed && snapshot.enabled ? 'suspended' : undefined}>{status}</span></div>
    <p id={`${id}-description`}>Allow all current and future eligible companies to set up Zapier lead delivery. Only company owners can configure, test and enable their own connection.</p>
    <p>Turning this on does not connect any company, resume paused connections or send historical leads.</p>
    {snapshot && <form className="management-registration-form" onSubmit={event => { event.preventDefault(); if (canSave) onSave(); }} aria-busy={busy}>
      <div className="management-registration-choice"><label htmlFor={`${id}-enabled`}>
        <input id={`${id}-enabled`} type="checkbox" checked={draft} disabled={!confirmed || busy || (!snapshot.canEnable && !draft)}
          aria-describedby={`${id}-description ${id}-readiness`} onChange={event => onChange(event.target.checked)} />
        Allow company owners to set up Zapier
      </label><small>{confirmed ? 'Saved global setting' : 'Last confirmed global setting'}: {snapshot.enabled ? 'On' : 'Off'}</small></div>
      <button type="submit" className="management-primary" disabled={!canSave}>Save</button>
    </form>}
    {snapshot?.scope === 'enrolled_companies' && <p>Legacy company enrollment is configured; global availability is off. Turning this on makes Zapier available to all eligible companies. Saving Off also stops any legacy enrolled delivery.</p>}
    <p id={`${id}-readiness`} className="management-registration-readiness">{snapshot && platformZapierReadinessMessage(snapshot.blockedReason)}</p>
    <div className="management-registration-feedback"><p ref={feedbackRef} tabIndex={-1} role={['unknown', 'error', 'forbidden'].includes(phase) ? 'alert' : 'status'}>{notice || (phase === 'loading' ? 'Loading Zapier availability…' : '')}</p>
      <button type="button" disabled={busy} onClick={onReload}>Reload settings</button></div>
  </section>;
}

function PlatformZapierSettingsController() {
  const [snapshot, setSnapshot] = useState(null), [draft, setDraft] = useState(false);
  const [phase, setPhase] = useState('loading'), [notice, setNotice] = useState('');
  const active = useRef(null), sequence = useRef(0), pending = useRef(false);
  const feedback = useRef(null), focusFeedback = useRef(false), alive = useRef(false);
  const confirmation = useConfirmationDialog();
  function begin() {
    active.current?.abort();
    const controller = new AbortController(); active.current = controller;
    const requestId = ++sequence.current;
    const timeout = setTimeout(() => controller.abort(), 15_000);
    pending.current = true;
    return { signal: controller.signal, current: () => alive.current && sequence.current === requestId,
      finish: () => { clearTimeout(timeout); if (sequence.current === requestId) { pending.current = false; active.current = null; } } };
  }
  async function load(successNotice = '') {
    const request = begin(); setPhase('loading'); setNotice('');
    try {
      const next = checkedPlatformZapierSettings(await platformZapierSettingsApi.get({ signal: request.signal }));
      if (!request.current()) return;
      setSnapshot(next); setDraft(next.enabled); setPhase('ready'); setNotice(successNotice);
    } catch (error) {
      if (!request.current()) return;
      setPhase(error?.status === 403 ? 'forbidden' : 'error');
      setNotice(error?.status === 403 ? 'You do not have access to Zapier availability.' : 'Zapier availability could not be loaded. Reload before making a change.');
    } finally { request.finish(); }
  }
  async function save() {
    if (pending.current || phase !== 'ready' || !canSavePlatformZapierSettings(snapshot, draft)) return;
    const body = { enabled: draft, expectedRevision: snapshot.revision };
    // Lock before awaiting the dialog. Unmount/revocation cancels its intent.
    pending.current = true;
    if (!body.enabled) {
      const accepted = await confirmation.request({ title: 'Turn off Zapier for all companies?',
        description: 'This pauses enabled connections and cancels unsent deliveries and pending tests. After availability is turned on again, owners must resume their paused connections. Already-authorized sends may still finish.',
        confirmLabel: 'Turn off Zapier', tone: 'danger' });
      if (!alive.current) return;
      if (!accepted) { pending.current = false; return; }
    }
    const request = begin(); setPhase('saving'); setNotice(''); focusFeedback.current = true;
    try {
      const next = checkedPlatformZapierSettingsSave(await platformZapierSettingsApi.update(body, { signal: request.signal }), body);
      if (!request.current()) return;
      setSnapshot(next); setDraft(next.enabled); setPhase('ready'); setNotice('Zapier availability saved.');
    } catch (error) {
      if (!request.current()) return;
      if (error?.status === 409) {
        await load('Zapier availability or readiness changed. Latest settings loaded. Review before saving.');
      } else if (error?.status === 403 || error?.status === 400) {
        setPhase(error.status === 403 ? 'forbidden' : 'error');
        setNotice(error.status === 403 ? 'You no longer have permission to change Zapier availability.' : 'This change was not accepted. Reload settings before trying again.');
      } else {
        setPhase('unknown'); setNotice('Save could not be confirmed. Reload settings before making another change.');
      }
    } finally { request.finish(); }
  }
  useEffect(() => { alive.current = true; load(); return () => { alive.current = false; sequence.current++; active.current?.abort(); pending.current = false; }; }, []);
  useEffect(() => {
    if (focusFeedback.current && notice && !['loading', 'saving'].includes(phase)) { feedback.current?.focus(); focusFeedback.current = false; }
  }, [phase, notice]);
  return <><PlatformZapierSettingsView snapshot={snapshot} draft={draft} phase={phase} notice={notice} feedbackRef={feedback}
    confirming={confirmation.dialogProps.open} onChange={value => { setDraft(value); setNotice(''); }} onSave={save}
    onReload={() => { if (!pending.current) { focusFeedback.current = true; load('Latest settings loaded. Review before saving.'); } }} />
    <ConfirmationDialog {...confirmation.dialogProps} /></>;
}

export default function PlatformZapierSettings({ platform }) {
  return canManagePlatformZapierSettings(platform) ? <PlatformZapierSettingsController /> : null;
}
